import React, { useState } from 'react';
import { useNotifications } from '../contexts/NotificationsContext';
import { BellOff, Trash2, Check } from 'lucide-react';
import { ChefHeader, Sheet } from '../components/ios';
import {
  notificationTypeMeta,
  formatTimeAgo,
  NotificationDetailBody,
  isAdminBroadcast,
  AdminBroadcastIcon
} from './NotificationDetailPage';

// Mirrors NotificationRowView in CustomerNotificationView.swift:
// 40pt tinted icon circle, title (16 semibold) + relative timestamp,
// 3-line message, driver arrival photo preview, trailing blue unread dot.
// iOS trailing swipe actions (Read / Delete) become trailing icon buttons.
function NotificationRow({ notification, onTap, onDelete, onMarkRead }) {
  const meta = notificationTypeMeta(notification.type);
  // iOS attaches the doorstep photo to driver_arrived; the web driver also
  // carries it on delivery_completed when the photo came after arrival.
  const arrivalImageUrl =
    ['driver_arrived', 'delivery_completed'].includes(notification.type)
      ? notification.data?.arrivalImageUrl
      : null;

  return (
    <div className="flex gap-3 p-3 bg-white rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
      {/* Tappable row body — opens the detail sheet */}
      <button
        onClick={onTap}
        className="flex items-center gap-3 flex-1 min-w-0 text-left active:opacity-70 transition-opacity"
      >
        {isAdminBroadcast(notification) ? (
          <AdminBroadcastIcon size={40} />
        ) : (
          <span
            className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${meta.color}1A` }}
          >
            <meta.Icon size={18} style={{ color: meta.color }} />
          </span>
        )}

        <span className="flex-1 min-w-0">
          <span className="flex items-start justify-between gap-2">
            <span className="text-[16px] font-semibold text-gray-900 leading-snug">
              {notification.title}
            </span>
            <span className="text-[12px] text-[#8E8E93] shrink-0 pt-0.5">
              {formatTimeAgo(notification.createdAt)}
            </span>
          </span>

          <span className="block text-[14px] text-[#6D6D72] line-clamp-3 mt-0.5">
            {notification.message}
          </span>

          {arrivalImageUrl && (
            <span className="block mt-1.5">
              <span className="block text-[12px] font-medium text-[#8E8E93] mb-1">
                Driver Location Photo
              </span>
              <img
                src={arrivalImageUrl}
                alt="Driver location"
                className="w-full h-20 object-cover rounded-lg"
              />
            </span>
          )}
        </span>
      </button>

      {/* Trailing column: unread dot + web equivalents of the iOS swipe actions */}
      <div className="flex flex-col items-center justify-center gap-1 shrink-0 self-center">
        {!notification.isRead && (
          <span className="w-2 h-2 rounded-full bg-[#007AFF]" />
        )}
        {!notification.isRead && (
          <button
            onClick={onMarkRead}
            className="p-1.5 text-[#007AFF] active:opacity-60"
            title="Mark as read"
            aria-label="Mark as read"
          >
            <Check size={16} />
          </button>
        )}
        <button
          onClick={onDelete}
          className="p-1.5 text-[#C7C7CC] hover:text-[#FF3B30] transition-colors"
          title="Delete notification"
          aria-label="Delete notification"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
}

function NotificationsPage() {
  const {
    notifications,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification
  } = useNotifications();
  const [selectedId, setSelectedId] = useState(null);

  // Resolve live so the sheet reflects mark-as-read updates
  const selectedNotification = notifications.find(n => n.id === selectedId) || null;

  const handleNotificationTap = (notification) => {
    setSelectedId(notification.id);
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
  };

  const handleDelete = async (notificationId, e) => {
    e.stopPropagation();
    await deleteNotification(notificationId);
  };

  const handleMarkAsRead = async (notificationId, e) => {
    e.stopPropagation();
    await markAsRead(notificationId);
  };

  return (
    <div className="min-h-screen bg-[#F2F2F7]">
      <ChefHeader title="Notifications" />

      {loading ? (
        <div className="flex items-center justify-center py-32">
          <div className="loading-spinner" />
        </div>
      ) : error ? (
        <div className="px-6 py-16 text-center">
          <p className="text-[15px] text-[#FF3B30]">{error}</p>
        </div>
      ) : notifications.length === 0 ? (
        /* Empty state — bell.slash + title2 + secondary message */
        <div className="flex flex-col items-center justify-center px-6 py-32 space-y-4">
          <BellOff size={64} className="text-[#8E8E93]" strokeWidth={1.25} />
          <h2 className="text-[22px] font-semibold text-gray-900">No Notifications</h2>
          <p className="text-[15px] text-[#6D6D72]">You're all caught up!</p>
        </div>
      ) : (
        <>
          {/* List — rounded rows on the grouped gray background */}
          <div className="max-w-lg md:max-w-2xl lg:max-w-3xl mx-auto px-4 pt-4 space-y-2">
            {notifications.map((notification) => (
              <NotificationRow
                key={notification.id}
                notification={notification}
                onTap={() => handleNotificationTap(notification)}
                onDelete={(e) => handleDelete(notification.id, e)}
                onMarkRead={(e) => handleMarkAsRead(notification.id, e)}
              />
            ))}
          </div>

          {/* "Mark All as Read" — red capsule, subheadline semibold, padded 16 */}
          <div className="max-w-lg md:max-w-2xl lg:max-w-3xl mx-auto p-4">
            <button
              onClick={markAllAsRead}
              className="w-full bg-[#E5E5EA] text-[#8E8E93] text-[15px] font-semibold py-2 min-h-[32px] rounded-full active:opacity-80 transition-opacity"
            >
              Mark All as Read
            </button>
          </div>
        </>
      )}

      {/* Notification detail — iOS .sheet equivalent */}
      <Sheet
        open={!!selectedNotification}
        onClose={() => setSelectedId(null)}
        detent="large"
        title="Notification"
      >
        <div className="px-4 pb-8 pt-1">
          {selectedNotification && (
            <NotificationDetailBody notification={selectedNotification} />
          )}
        </div>
      </Sheet>
    </div>
  );
}

export default NotificationsPage;
